import { createReducer, on } from '@ngrx/store';
import { SessionActions } from './session.actions';
import { ChatMessage } from '../../../core/models/chat-message.model';
import { HandoffResponse } from '../../../core/models/handoff.model';
import { SlidePreview } from '../../../core/models/slide-preview.model';
import { Phase, SessionInputPayload, SessionStatus } from '../../../core/models/session.model';

export type ConnectionState = 'connected' | 'disconnected' | 'reconnecting';

export type SessionState = {
  readonly sessionId: string | null;
  readonly status: SessionStatus | null;
  readonly clientName: string | null;
  readonly proposalId: string | null;
  readonly currentPhase: Phase | null;
  readonly websocketUrl: string | null;
  readonly logoUrl: string | null;
  readonly handoff: HandoffResponse | null;
  readonly userInput: SessionInputPayload | null;
  readonly messages: ReadonlyArray<ChatMessage>;
  readonly slides: ReadonlyArray<SlidePreview>;
  readonly deckVersion: number;
  readonly totalSlides: number;
  readonly connectionState: ConnectionState;
  readonly isLoading: boolean;
  readonly isAgentTyping: boolean;
  readonly exportDownloadUrl: string | null;
  readonly exportFileName: string | null;
  readonly exportExpiresAt: string | null;
  readonly error: string | null;
  readonly errorRetryAvailable: boolean;
};

export const initialState: SessionState = {
  sessionId: null,
  status: null,
  clientName: null,
  proposalId: null,
  currentPhase: null,
  websocketUrl: null,
  logoUrl: null,
  handoff: null,
  userInput: null,
  messages: [],
  slides: [],
  deckVersion: 0,
  totalSlides: 0,
  connectionState: 'disconnected',
  isLoading: false,
  isAgentTyping: false,
  exportDownloadUrl: null,
  exportFileName: null,
  exportExpiresAt: null,
  error: null,
  errorRetryAvailable: false,
};

export const sessionReducer = createReducer(
  initialState,

  on(SessionActions.createSession, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),

  on(SessionActions.createSessionSuccess, (state, { session }) => ({
    ...state,
    sessionId: session.sessionId,
    status: session.status,
    clientName: session.clientName,
    proposalId: session.proposalId,
    currentPhase: session.currentPhase,
    websocketUrl: session.websocketUrl,
    isLoading: false,
  })),

  on(SessionActions.createSessionFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(SessionActions.submitInput, (state, { payload }) => ({
    ...state,
    isLoading: true,
    error: null,
    userInput: payload,
  })),

  on(SessionActions.submitInputSuccess, (state) => ({
    ...state,
    isLoading: false,
    status: 'IN_PROGRESS' as const,
  })),

  on(SessionActions.submitInputFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(SessionActions.uploadLogoFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  on(SessionActions.uploadLogoSuccess, (state, { asset }) => ({
    ...state,
    logoUrl: asset.url,
  })),

  on(SessionActions.loadHandoffSuccess, (state, { handoff }) => ({
    ...state,
    handoff,
  })),

  on(SessionActions.websocketConnected, (state) => ({
    ...state,
    connectionState: 'connected' as const,
    error: null,
    errorRetryAvailable: false,
  })),

  on(SessionActions.websocketDisconnected, (state) => ({
    ...state,
    connectionState: 'disconnected' as const,
  })),

  on(SessionActions.websocketReconnecting, (state) => ({
    ...state,
    connectionState: 'reconnecting' as const,
  })),

  on(SessionActions.messageSent, (state, { message }) => ({
    ...state,
    messages: [...state.messages, {
      id: crypto.randomUUID(),
      type: 'user' as const,
      content: message.content,
      phase: state.currentPhase ?? 'OPENING' as Phase,
      turnNumber: state.messages.length + 1,
      timestamp: message.timestamp,
    }],
    isAgentTyping: true,
  })),

  on(SessionActions.messageReceived, (state, { message }) => ({
    ...state,
    messages: [...state.messages, message],
    isAgentTyping: false,
  })),

  on(SessionActions.previewReceived, (state, { slides, totalSlides, deckVersion }) => {
    // Don't overwrite existing slides with empty preview URLs (from WS deck_preview with relative paths)
    const hasValidUrls = slides.some((s) => !!s.previewUrl);
    if (!hasValidUrls && state.slides.length > 0) {
      // Keep existing slides, just update metadata
      return { ...state, totalSlides, deckVersion };
    }
    return { ...state, slides, totalSlides, deckVersion };
  }),

  on(SessionActions.phaseChanged, (state, { phase }) => ({
    ...state,
    currentPhase: phase ?? state.currentPhase,
  })),

  on(SessionActions.exportReady, (state, { downloadUrl, fileName, expiresAt }) => ({
    ...state,
    status: 'EXPORTED' as const,
    currentPhase: 'EXPORT' as const,
    exportDownloadUrl: downloadUrl,
    exportFileName: fileName,
    exportExpiresAt: expiresAt,
  })),

  on(SessionActions.errorReceived, (state, { message, retryAvailable }) => ({
    ...state,
    error: message,
    errorRetryAvailable: retryAvailable,
    isAgentTyping: false,
  })),

  on(SessionActions.hydrateSession, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),

  on(SessionActions.hydrateSessionSuccess, (state, { session, messages, slides, handoff }) => {
    // Derive current phase: prefer session.currentPhase, fall back to last message's phase
    const lastMessagePhase = messages.length > 0 ? messages[messages.length - 1].phase : null;
    const currentPhase = session.currentPhase ?? lastMessagePhase ?? state.currentPhase;

    return {
      ...state,
      sessionId: session.sessionId,
      status: session.status,
      clientName: session.clientName,
      proposalId: session.proposalId,
      currentPhase,
      websocketUrl: session.websocketUrl,
      handoff: handoff ?? state.handoff,
      messages,
      slides,
      totalSlides: slides.length,
      isLoading: false,
    };
  }),

  on(SessionActions.hydrateSessionFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(SessionActions.restoreLocalState, (state, { userInput, logoUrl }) => ({
    ...state,
    userInput: userInput ?? state.userInput,
    logoUrl: logoUrl ?? state.logoUrl,
  })),
);
